import { Procedure, CreateProcedure } from './procedure/entity';

export type StockItem = {
  id?: string;
  itemName: string;
  unit: Unit;
  totalPrice: number;
  quantity: number;
  threshold: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  stockId: string;
  packageCount: number;
  avgUnitPrice: number;
  lastPackageQuantity: number;
  itemType: 'MATERIAL' | 'GOODS';
  retailPrice?: number;
  brand?: string;
};

export type ProcedureTypeEnum = 'SALE' | 'CONSUMPTION';

export const filterProceduresByType = (
  procedures: CreateProcedure[],
  type: ProcedureTypeEnum,
): CreateProcedure[] => {
  return procedures.filter(procedure => procedure.type === type);
};

export const hasAnyStockAllowance = (procedures?: (Procedure | CreateProcedure)[]): boolean => {
  if (!procedures || procedures.length === 0) {
    return false;
  }

  return procedures.some(p => p.stockAllowances && p.stockAllowances.length > 0);
};

export const getMissingStockAllowanceError = (
  t: (key: string) => string,
  procedures?: (Procedure | CreateProcedure)[],
): string | undefined => {
  if (!hasAnyStockAllowance(procedures)) {
    return t('visitDetailGrid.stockAllowanceWarning');
  }
};

export type Stock = {
  id: string;
  stockName: string;
  createdAt: Date;
  unit: Unit;
  ownerId: string;
  price: number;
};

export type StockItemFormUsagePurposeType = 'purchase' | 'purchaseAndNewStockItem' | 'stockItem';

export type StockItemDefaultValuesType = Pick<
  StockItem,
  | 'quantity'
  | 'totalPrice'
  | 'id'
  | 'threshold'
  | 'itemType'
  | 'unit'
  | 'itemName'
  | 'stockId'
  | 'packageCount'
  | 'avgUnitPrice'
  | 'lastPackageQuantity'
  | 'itemType'
  | 'retailPrice'
  | 'brand'
>;

export type ExistingStockItem = {
  id: string;
  itemName: string;
  unit: Unit;
  totalPrice: number;
  quantity: number;
  threshold: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  stockId: string;
  packageCount: number;
  avgUnitPrice: number;
  lastPackageQuantity: number;
  itemType: 'MATERIAL' | 'GOODS';
  retailPrice?: number;
  brand?: string;
};

export type StockItemCreateData = Omit<
  StockItem,
  'createdAt' | 'updatedAt' | 'isActive' | 'lastPackageQuantity'
> & {
  stockId: string;
};

export type StockItemBuyData = {
  id: string;
  price: number;
  quantity: number;
};

export const UnitsObject = {
  G: 'g',
  ML: 'ml',
  KS: 'ks',
};

export type StockAllowance = {
  createdAt?: Date;
  id?: string;
  procedureId?: string;
  quantity: string;
  type: 'CONSUMPTION' | 'SALE';
  stockItem?: StockItem;
  stockItemId: string;
  userId?: string;
};

export type StockAllowanceFieldArrayType = {
  stockItemId: string;
  quantity: number;
  id: string;
};

export type ReceiptIntakeItem = {
  itemName: string;
  matchedStockItemId: string | null;
  matchedStockItemName: string | null;
  quantity: number;
  packageCount: number;
  totalPrice: number;
  unit: Unit;
};

export type StockIntakePurchasePayload = {
  id: string;
  quantity: number;
  totalPrice: number;
  packageCount: number;
  stockId: string;
};

export type StockIntakePayload = StockIntakePurchasePayload | StockItemCreateData;

export type Unit = (typeof UnitsObject)[keyof typeof UnitsObject];
export type StockWithStockItems = Stock & { stockItems: ExistingStockItem[] };

export const mapStocksStockItemsToFlatStockItems = (
  stockWithstockItems?: StockWithStockItems[],
): ExistingStockItem[] | undefined => {
  if (!stockWithstockItems) {
    return;
  }
  return stockWithstockItems.flatMap(stock => stock.stockItems);
};

export enum StockItemView {
  all = 'all',
  material = 'material',
  goods = 'goods',
}

export type StockItemViewKey = keyof typeof StockItemView;

export const STOCK_ITEM_FILTER_BUTTONS_CONFIG: Record<
  StockItemViewKey,
  { defaultMessage: string; id: string; requiresExpert?: boolean }
> = {
  [StockItemView.all]: { defaultMessage: 'Vše', id: 'stock.filter.all' },
  [StockItemView.material]: { defaultMessage: 'Materiál', id: 'stock.filter.material' },
  [StockItemView.goods]: {
    defaultMessage: 'Zboží',
    id: 'stock.filter.goods',
    requiresExpert: true,
  },
};

export const filterStockItemsByView = (
  items: ExistingStockItem[],
  view: StockItemViewKey,
): ExistingStockItem[] => {
  if (view === StockItemView.material) return items.filter(i => i.itemType === 'MATERIAL');
  if (view === StockItemView.goods) return items.filter(i => i.itemType === 'GOODS');
  return items;
};
